import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';
import api, { API_ENDPOINTS } from '../config/api';
import BlogPreviewColumns from '../components/BlogPreviewColumns';
import { BookOpen, Users, UserPlus, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const ProfilePage = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('saved');
  const [savedPage, setSavedPage] = useState(1);
  const [followingPage, setFollowingPage] = useState(1);
  const limit = 9;

  // Fetch saved blogs
  const { data: savedData, isLoading: savedLoading } = useQuery({
    queryKey: ['blogs', 'saved', savedPage],
    queryFn: async () => {
      const response = await api.get(API_ENDPOINTS.BLOGS.SAVED, {
        params: { page: savedPage, limit },
      });
      return response.data;
    },
    enabled: activeTab === 'saved',
  });

  // Fetch following blogs
  const { data: followingData, isLoading: followingLoading } = useQuery({
    queryKey: ['blogs', 'following', followingPage],
    queryFn: async () => {
      const response = await api.get(API_ENDPOINTS.BLOGS.FOLLOWING, {
        params: { page: followingPage, limit },
      });
      return response.data;
    },
    enabled: activeTab === 'following',
  });

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-500 dark:text-gray-500">Please log in to view your profile.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Profile Header */}
        <div className="card p-6 mb-8">
          <div className="flex flex-col md:flex-row items-start md:items-center space-y-4 md:space-y-0 md:space-x-6">
            {/* Avatar and Username */}
            <div className="flex items-center space-x-4">
              {user.profile?.avatar ? (
                <img
                  src={user.profile.avatar}
                  alt={user.username}
                  className="w-24 h-24 md:w-32 md:h-32 rounded-full flex-shrink-0"
                />
              ) : (
                <div className="w-24 h-24 md:w-32 md:h-32 bg-accent rounded-full flex items-center justify-center text-white text-3xl md:text-4xl font-medium flex-shrink-0">
                  {user.username?.charAt(0).toUpperCase() || 'U'}
                </div>
              )}
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-heading dark:text-white">
                {user?.username || user?.email || 'User'}
              </h1>
            </div>
            
            {/* User Info */}
            <div className="flex-1 w-full">
              <div className="mb-4">
                {(user.profile?.firstName || user.profile?.lastName) && (
                  <p className="text-lg text-gray-600 dark:text-gray-400">
                    {[user.profile?.firstName, user.profile?.lastName].filter(Boolean).join(' ')}
                  </p>
                )}
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                {user.email && (
                  <div className="flex items-center text-gray-600 dark:text-gray-400">
                    <span className="font-medium mr-2">Email:</span>
                    <span>{user.email}</span>
                  </div>
                )}
                {user.walletAddress && (
                  <div className="flex items-center text-gray-600 dark:text-gray-400">
                    <span className="font-medium mr-2">Wallet:</span>
                    <span className="font-mono text-xs">{user.walletAddress.slice(0, 6)}...{user.walletAddress.slice(-4)}</span>
                  </div>
                )}
                {user.createdAt && (
                  <div className="flex items-center text-gray-600 dark:text-gray-400">
                    <span className="font-medium mr-2">Joined:</span>
                    <span>{new Date(user.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                  </div>
                )}
                {user.role && (
                  <div className="flex items-center text-gray-600 dark:text-gray-400">
                    <span className="font-medium mr-2">Role:</span>
                    <span className="capitalize">{user.role}</span>
                  </div>
                )}
              </div>
              
              {user.profile?.bio && (
                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <p className="text-gray-600 dark:text-gray-400">{user.profile.bio}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex space-x-4 mb-6 border-b border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setActiveTab('saved')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'saved'
                ? 'border-b-2 border-accent text-accent'
                : 'text-gray-600 dark:text-gray-400 hover:text-accent'
            }`}
          >
            <BookOpen className="w-4 h-4 inline mr-2" />
            Saved Blogs
          </button>
          <button
            onClick={() => setActiveTab('following')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'following'
                ? 'border-b-2 border-accent text-accent'
                : 'text-gray-600 dark:text-gray-400 hover:text-accent'
            }`}
          >
            <UserPlus className="w-4 h-4 inline mr-2" />
            Following
          </button>
        </div>

        {/* Content */}
        {activeTab === 'saved' && (
          <div>
            {savedLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-accent" />
              </div>
            ) : savedData?.blogs?.length === 0 ? (
              <div className="text-center py-12">
                <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500 dark:text-gray-500">
                  You haven't saved any blogs yet.
                </p>
              </div>
            ) : (
              <>
                <BlogPreviewColumns blogs={savedData?.blogs || []} />
                {savedData?.pagination && savedData.pagination.totalPages > 1 && (
                  <div className="flex items-center justify-center space-x-2 mt-12">
                    <button
                      onClick={() => setSavedPage((p) => Math.max(1, p - 1))}
                      disabled={savedPage === 1}
                      className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center"
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" />
                      Previous
                    </button>
                    <span className="px-4 py-2">
                      Page {savedPage} of {savedData.pagination.totalPages}
                    </span>
                    <button
                      onClick={() => setSavedPage((p) => Math.min(savedData.pagination.totalPages, p + 1))}
                      disabled={savedPage === savedData.pagination.totalPages}
                      className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center"
                    >
                      Next
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {activeTab === 'following' && (
          <div>
            {followingLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-accent" />
              </div>
            ) : followingData?.blogs?.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500 dark:text-gray-500">
                  You're not following any bloggers yet.
                </p>
              </div>
            ) : (
              <>
                <BlogPreviewColumns blogs={followingData?.blogs || []} />
                {followingData?.pagination && followingData.pagination.totalPages > 1 && (
                  <div className="flex items-center justify-center space-x-2 mt-12">
                    <button
                      onClick={() => setFollowingPage((p) => Math.max(1, p - 1))}
                      disabled={followingPage === 1}
                      className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center"
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" />
                      Previous
                    </button>
                    <span className="px-4 py-2">
                      Page {followingPage} of {followingData.pagination.totalPages}
                    </span>
                    <button
                      onClick={() => setFollowingPage((p) => Math.min(followingData.pagination.totalPages, p + 1))}
                      disabled={followingPage === followingData.pagination.totalPages}
                      className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center"
                    >
                      Next
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfilePage;
